import { useSelector } from "react-redux";
import { getLang } from "store/userSlice";
import { useTranslation } from "translation/useTranslation";

/**
 * German and English wording for the Bewertungszeiträume screens.
 *
 * The dictionary the app runs on is maintained outside the repository, so a key
 * that has not been registered there yet would otherwise render raw. Every key
 * below carries both languages as its fallback, which keeps the screens readable
 * in either language until the dictionary catches up.
 *
 * The takeover wording carries a key PER MODULE wherever the modules differ.
 * Budgets, Leistungskarten and Teamziele exist in LOB only and must not appear
 * on a DB period, and building one string set with the module name patched into
 * it is exactly what produced the wrong text before.
 */
const STRINGS = {
  "period_management.intro.both_modules": {
    de: "Jede Periode gehört zu genau einem Modul und schließt i.d.R. lückenlos an ihre Vorgängerin an. Perioden verschiedener Module dürfen sich überlappen.",
    en: "Every period belongs to exactly one module and normally follows its predecessor without a gap. Periods of different modules may overlap.",
  },
  "period_management.intro.single_module": {
    de: "Jede Periode schließt i.d.R. lückenlos an ihre Vorgängerin an. Zwei Perioden dürfen sich nie überschneiden, eine Lücke dazwischen ist erlaubt.",
    en: "Every period normally follows its predecessor without a gap. Two periods may never overlap, a gap between them is allowed.",
  },

  "period_management.status.live": { de: "Aktiv", en: "Active" },
  "period_management.status.draft": { de: "Entwurf", en: "Draft" },
  "period_management.status.archived": { de: "Archiviert", en: "Archived" },
  "period_management.status.pending": { de: "In Arbeit", en: "In progress" },

  "period_management.duration_years_one": { de: "1 Jahr", en: "1 year" },
  "period_management.duration_years_other": { de: "{years} Jahre", en: "{years} years" },

  "period_management.legend.proposal": { de: "Vorschlag", en: "Proposal" },
  "period_management.legend.archived_hint": {
    de: "Archivierte Perioden erscheinen im Detailbereich",
    en: "Archived periods appear in the detail area",
  },

  "period_management.ghost.create_next": { de: "+ Nächste Periode anlegen", en: "+ Create next period" },
  "period_management.ghost.create_short": { de: "+ Anlegen", en: "+ Create" },
  "period_management.ghost.create_first": { de: "+ Erste Periode anlegen", en: "+ Create first period" },
  "period_management.ghost.tooltip": {
    de: "Nächste Periode {name} anlegen ({range})",
    en: "Create the next period {name} ({range})",
  },
  "period_management.ghost.tooltip_first": {
    de: "Erste Periode {name} anlegen ({range})",
    en: "Create the first period {name} ({range})",
  },
  "period_management.ghost.blocked": {
    de: "Das {module}-Modul hat sein Limit von zwei offenen Zeiträumen erreicht ({periods}). Archivieren Sie einen davon oder verwerfen Sie einen Entwurf, bevor Sie einen weiteren Zeitraum anlegen.",
    en: "The {module} module has reached its limit of two open periods ({periods}). Archive one of them, or discard a draft, before creating another period.",
  },
  "period_management.notice_title": { de: "Bitte beachten Sie:", en: "Please note:" },

  "period_management.detail.status": { de: "Status", en: "Status" },
  "period_management.detail.evaluation_period": { de: "Beurteilungszeitraum", en: "Evaluation period" },
  "period_management.detail.active_employees": { de: "Aktive Beschäftigte", en: "Active employees" },
  "period_management.detail.source_period": { de: "Quellperiode", en: "Source period" },
  "period_management.detail.no_source": { de: "keine (erste Periode im Modul)", en: "none (first period of the module)" },
  "period_management.detail.no_source_other": { de: "keine", en: "none" },
  "period_management.detail.no_range": {
    de: "nicht festgelegt, bitte über „Bearbeiten“ ergänzen",
    en: "not set, please add it via „Edit“",
  },
  "period_management.detail.no_employees": { de: "noch keine", en: "none yet" },
  "period_management.detail.lineage": { de: "Abfolge im Modul {module}", en: "Sequence in module {module}" },
  "period_management.detail.locks_title": {
    de: "Was für diese Periode nicht möglich ist",
    en: "What is not possible for this period",
  },
  "period_management.detail.copying": {
    de: "Datenübernahme läuft, noch nicht auswählbar.",
    en: "The takeover is still running, not selectable yet.",
  },
  "period_management.detail.empty": {
    de: "Wählen Sie eine Periode in der Zeitleiste, um ihre Angaben zu sehen.",
    en: "Select a period in the timeline to see its details.",
  },
  "period_management.default_period": { de: "Standardperiode", en: "Default period" },
  "period_management.actions_menu": { de: "Aktionen", en: "Actions" },

  "period_management.lock.is_default_period": {
    de: "solange dies die Standardperiode ist",
    en: "while this is the default period",
  },
  "period_management.lock.only_drafts_deletable": {
    de: "nur Entwürfe können gelöscht werden",
    en: "only drafts can be deleted",
  },
  "period_management.lock.has_evaluations": {
    de: "an dieser Periode hängen Auswertungen",
    en: "evaluations depend on this period",
  },
  "period_management.lock.is_current_period": {
    de: "solange Sie in dieser Periode arbeiten",
    en: "while you are working in this period",
  },
  "period_management.lock.open_limit_reached": {
    de: "das {module}-Modul sein Limit von zwei offenen Zeiträumen erreicht hat ({periods}); archivieren Sie einen davon oder verwerfen Sie einen Entwurf",
    en: "the {module} module has reached its limit of two open periods ({periods}); archive one of them or discard a draft",
  },
  "period_management.lock.join_word": { de: "und", en: "and" },

  "period_management.action.set_live": { de: "Live schalten", en: "Set live" },
  "period_management.action.make_default": { de: "Als Standardperiode setzen", en: "Make default period" },
  "period_management.action.set_edit": { de: "Bearbeiten", en: "Edit" },
  "period_management.action.set_draft": { de: "Zurück zu Entwurf", en: "Back to draft" },
  "period_management.action.set_archived": { de: "Archivieren", en: "Archive" },
  "period_management.action.set_deleted": { de: "Löschen", en: "Delete" },
  "period_management.action.reactivate": { de: "Wieder aktivieren", en: "Reactivate" },

  "period_management.confirm.set_archived.title": { de: "Periode archivieren?", en: "Archive period?" },
  "period_management.confirm.set_archived.body": {
    de: "{name} verschwindet aus der aktiven Auswahl. Auswertungen bleiben erhalten und die Periode kann jederzeit wieder aktiviert werden.",
    en: "{name} disappears from the active selection. Evaluations are kept and the period can be reactivated at any time.",
  },
  "period_management.confirm.set_archived.ok": { de: "Archivieren", en: "Archive" },
  "period_management.confirm.set_draft.title": { de: "Zurück zu Entwurf?", en: "Back to draft?" },
  "period_management.confirm.set_draft.body": {
    de: "{name} ist danach nicht mehr produktiv auswählbar. Bereits erfasste Daten bleiben erhalten.",
    en: "{name} can no longer be selected in production afterwards. Data already entered is kept.",
  },
  "period_management.confirm.set_draft.ok": { de: "Zurückstufen", en: "Demote" },
  "period_management.confirm.set_deleted.title": { de: "Periode löschen?", en: "Delete period?" },
  "period_management.confirm.set_deleted.body": {
    de: "{name} wird ausgeblendet und ist danach nicht mehr wählbar. Es gehen keine Daten verloren.",
    en: "{name} is hidden and can no longer be selected. No data is lost.",
  },
  "period_management.confirm.set_deleted.ok": { de: "Löschen", en: "Delete" },
  "period_management.confirm.cancel": { de: "Abbrechen", en: "Cancel" },

  "period_management.new": { de: "Neu", en: "New" },
  "period_management.edit": { de: "Bearbeiten", en: "Edit" },
  "period_management.create.title": { de: "Zeitraum anlegen", en: "Create period" },
  "period_management.create.subtitle": {
    de: "Die neue Periode entsteht als Entwurf und übernimmt Daten aus ihrer Vorgängerin. Sie wird erst durch „Live schalten“ produktiv.",
    en: "The new period is created as a draft and takes data over from its predecessor. It only goes into production through „Set live“.",
  },
  "period_management.create.subtitle_first": {
    de: "Der erste Zeitraum des Moduls entsteht als Entwurf mit den Standardeinstellungen. Er wird erst durch „Live schalten“ produktiv.",
    en: "The module's first period is created as a draft with the default settings. It only goes into production through „Set live“.",
  },
  "period_management.section.identification": { de: "Bezeichnung", en: "Identification" },
  "period_management.section.takeover": { de: "Übernahme", en: "Takeover" },
  "period_management.field.name": { de: "Name", en: "Name" },
  "period_management.field.description": { de: "Beschreibung", en: "Description" },
  "period_management.field.optional": { de: "optional", en: "optional" },
  "period_management.field.description_hint": {
    de: "Erscheint in der Übersicht unter dem Namen.",
    en: "Appears in the overview below the name.",
  },
  "period_management.field.description_placeholder": {
    de: "z. B. Regelzyklus {year}",
    en: "e.g. regular cycle {year}",
  },
  "period_management.name.free": { de: "Name ist frei.", en: "Name is available." },
  "period_management.name.taken": {
    de: "Dieser Name ist bereits vergeben. Namen sind über alle Module hinweg eindeutig.",
    en: "This name is already taken. Names are unique across all modules.",
  },
  "period_management.name.required": {
    de: "Pflichtfeld. Bitte einen Namen vergeben.",
    en: "Required. Please enter a name.",
  },

  "period_management.date.default_duration_one": {
    de: "1 Jahr gemäß Standardeinstellungen.",
    en: "1 year according to the default settings.",
  },
  "period_management.date.default_duration_other": {
    de: "{years} Jahre gemäß Standardeinstellungen.",
    en: "{years} years according to the default settings.",
  },
  "period_management.date.end_before_start": {
    de: "Das Enddatum muss nach dem Startdatum liegen.",
    en: "The end date has to be after the start date.",
  },
  "period_management.date.start_before_source": {
    de: "Der Start muss nach dem Enddatum der Quellperiode ({date}) liegen.",
    en: "The start has to be after the end date of the source period ({date}).",
  },
  "period_management.date.months": { de: "{months} Monate.", en: "{months} months." },

  "period_management.join.seamless": {
    de: "Die neue Periode beginnt am Tag nach dem Ende von {name}.",
    en: "The new period starts the day after {name} ends.",
  },
  "period_management.join.gap": {
    de: "Lücke von {days}: In dieser Zeit ist im Modul {module} keine Periode aktiv.",
    en: "Gap of {days}: no period is active in module {module} during that time.",
  },
  "period_management.join.overlap": {
    de: "Überschneidung: Der Start muss nach dem {date} liegen.",
    en: "Overlap: the start has to be after {date}.",
  },
  "period_management.join.first": {
    de: "Erste Periode im Modul {module}.",
    en: "First period of module {module}.",
  },
  "period_management.lane.source": { de: "Quellperiode", en: "Source period" },
  "period_management.lane.new": { de: "Neue Periode", en: "New period" },
  "period_management.lane.previous": { de: "Vorgängerin", en: "Predecessor" },
  "period_management.lane.this": { de: "Diese Periode", en: "This period" },
  "period_management.lane.next": { de: "Nachfolgerin", en: "Successor" },
  "period_management.lane.undated": { de: "Ohne Beurteilungszeitraum:", en: "Without evaluation period:" },
  "period_management.lane.undated_hint": {
    de: "Dieser Periode fehlt der Beurteilungszeitraum. Öffnen und über „Bearbeiten“ ergänzen.",
    en: "This period has no evaluation period. Open it and add one via „Edit“.",
  },

  "period_management.days_one": { de: "1 Tag", en: "1 day" },
  "period_management.days_other": { de: "{days} Tagen", en: "{days} days" },

  "period_management.takeover.title": { de: "Übernahmeoptionen", en: "Takeover options" },
  "period_management.copy_mode.minimal": { de: "Minimal", en: "Minimal" },
  "period_management.copy_mode.full": { de: "Vollständig", en: "Full" },

  /* ---- Takeover, two presets plus a free selection, both modules (LIX-1696) ---- */

  "period_management.takeover.intro_db": {
    de: "Was soll aus dem Quellzeitraum {name} übernommen werden? Die Vorlagen setzen die Auswahl darunter.",
    en: "What should be taken over from the source period {name}? The templates set the selection below.",
  },
  "period_management.takeover.custom": { de: "Benutzerdefiniert", en: "Custom" },
  "period_management.takeover.irreversible": {
    de: "Nach dem Anlegen lässt sich die Übernahme nicht mehr ändern.",
    en: "After creation the takeover can no longer be changed.",
  },
  "period_management.takeover.preset_desc.minimal": {
    de: "Nur die Einstellungen. Keine Beschäftigten und keine Gruppen.",
    en: "The settings only. No employees and no groups.",
  },
  "period_management.takeover.preset_desc.full": {
    de: "Alle Bereiche und alle Angaben. Die Beschäftigten sind sofort sichtbar.",
    en: "All areas and all details. The employees are visible immediately.",
  },
  "period_management.takeover.preset_desc.custom": {
    de: "Abweichend von jeder Vorlage. Die Auswahl unten gilt.",
    en: "Different from every template. The selection below applies.",
  },

  "period_management.takeover.areas_title": { de: "Was wird übernommen", en: "What is taken over" },
  "period_management.takeover.area.always_note": {
    de: "unabhängig vom Zeitraum", en: "independent of the period",
  },

  "period_management.takeover.area.settings": { de: "Einstellungen", en: "Settings" },
  "period_management.takeover.area.settings.desc": {
    de: "Ansichten, Filter, Beurteilungsvorlagen, Gesprächseinstellungen",
    en: "Views, filters, evaluation templates, interview settings",
  },
  "period_management.takeover.area.global_roles": { de: "Globale Rollen", en: "Global roles" },
  "period_management.takeover.area.global_roles.desc": {
    de: "IT-Administration, Programmadministration, Moduladministration und Personalverwaltung. Diese Rollen gelten unabhängig vom Zeitraum und sind im neuen Zeitraum von Anfang an aktiv. Sie werden nicht übernommen, weil sie gar nicht am Zeitraum hängen.",
    en: "IT administration, program administration, module administration and HR support. These roles are independent of the period and are active in the new period from the start. They are not taken over because they do not belong to a period at all.",
  },
  "period_management.takeover.area.groups": { de: "Gruppen und Zuordnungen", en: "Groups and assignments" },
  "period_management.takeover.area.groups.desc.db": {
    de: "Gruppen mit ihren Erst- und Zweitbeurteilenden, dazu die Kategorien von Gruppen und Beschäftigten. Ohne Übernahme werden die Zuordnungen im neuen Zeitraum neu aufgebaut.",
    en: "Groups with their first and second evaluators, plus the categories of groups and employees. Without a takeover the assignments are rebuilt in the new period.",
  },
  "period_management.takeover.area.groups.desc.lob": {
    de: "Gruppen mit ihren Bewertenden und Rezensierenden, dazu die Kategorien von Gruppen und Beschäftigten. Ohne Übernahme werden die Zuordnungen im neuen Zeitraum neu aufgebaut.",
    en: "Groups with their evaluators and reviewers, plus the categories of groups and employees. Without a takeover the assignments are rebuilt in the new period.",
  },
  "period_management.takeover.area.employees": { de: "Beschäftigte", en: "Employees" },
  "period_management.takeover.area.employees.desc": {
    de: "Die Personen selbst. Welche Angaben mitkommen, steuert die Feldauswahl darunter.",
    en: "The people themselves. Which details come along is set by the field selection below.",
  },

  "period_management.takeover.fields_title": {
    de: "Angaben zu den Beschäftigten",
    en: "Employee details",
  },
  "period_management.takeover.fields_count": {
    de: "{count} von {total} ausgewählt",
    en: "{count} of {total} selected",
  },
  "period_management.takeover.fields_no_employees": {
    de: "Beschäftigte nicht ausgewählt",
    en: "Employees not selected",
  },
  "period_management.takeover.fields_free_note": {
    de: "Individuell angelegte Felder kommen immer mit und lassen sich deshalb nicht abwählen.",
    en: "Individually created fields always come along and therefore cannot be deselected.",
  },
  "period_management.takeover.bulk_all": { de: "Alle", en: "All" },
  "period_management.takeover.bulk_none": { de: "Keine", en: "None" },

  "period_management.takeover.field.department_staff_unit": {
    de: "Organisationseinheit", en: "Organisational unit",
  },
  "period_management.takeover.field.employee_group": { de: "Gruppe", en: "Group" },
  "period_management.takeover.field.working_hours": { de: "Arbeitsumfang", en: "Scope of work" },
  "period_management.takeover.field.gender": { de: "Geschlecht", en: "Gender" },
  "period_management.takeover.field.equal_opportunities_severe_disability_checkbox": {
    de: "Schwerbehinderung / Gleichstellung", en: "Severe disability / equal opportunities",
  },
  "period_management.takeover.field.state_employee_pay_grade": {
    de: "Eingruppierung / Statusamt", en: "Pay grade / status office",
  },
  "period_management.takeover.field.job_differs_from_classification": {
    de: "Abweichende Stellenbewertung", en: "Job evaluation differs from classification",
  },
  "period_management.takeover.field.job_title": { de: "Stellenbezeichnung", en: "Job title" },
  "period_management.takeover.field.job_description_text": {
    de: "Aufgabenbeschreibung", en: "Job description",
  },

  "period_management.takeover.always_title": {
    de: "Immer vorhanden, unabhängig von der Übernahme",
    en: "Always present, independent of the takeover",
  },
  "period_management.takeover.always_body": {
    de: "Nachname, Vorname, Personalnummer, E-Mailadresse, Eintrittsdatum. Diese Angaben gehören zur Person und nicht zum Zeitraum. Sie stehen im neuen Zeitraum ohne Übernahme zur Verfügung.",
    en: "Last name, first name, personnel number, email address, entry date. These belong to the person and not to the period. They are available in the new period without a takeover.",
  },
  "period_management.takeover.never_title": {
    de: "Wird nie übernommen, in keiner Variante",
    en: "Never taken over, in any variant",
  },
  "period_management.takeover.never_body": {
    de: "Beurteilungszeitraum, Beförderung / Höhergruppierung, Übertragung höherwertiger Tätigkeiten, Beurteilungsgrund bei Anlassbeurteilung, Vorheriger RBU (alte Skala). Diese Angaben beschreiben Vorgänge des abgelaufenen Zeitraums und bleiben dort unverändert erhalten.",
    en: "Evaluation period, promotion or upgrade, transfer of higher level tasks, reason for an event based evaluation, previous RBU on the old scale. These describe events of the period that ended and stay there unchanged.",
  },

  "period_management.takeover.previous_result_title": {
    de: "Vorheriges Regelbeurteilungsergebnis",
    en: "Previous regular evaluation result",
  },
  "period_management.takeover.previous_result_head": {
    de: "Wird nachgeschlagen, nicht übernommen",
    en: "Looked up, not taken over",
  },
  "period_management.takeover.previous_result_body": {
    de: "Der Wert wird bei jedem Aufruf aus der letzten abgeschlossenen Regelbeurteilung des Vorzeitraums gelesen und nicht im neuen Zeitraum gespeichert. Ändert sich die Beurteilung im Vorzeitraum später, weil sie erst dann abgeschlossen oder noch korrigiert wird, zeigt der neue Zeitraum sofort den aktuellen Stand. Ein alter Feldwert wird nie mitgenommen.",
    en: "The value is read from the last completed regular evaluation of the source period every time it is shown, and is not stored in the new period. If that evaluation changes later, because it is only completed or still corrected then, the new period shows the current state immediately. An old field value is never carried over.",
  },
  "period_management.takeover.previous_result_source": {
    de: "Quelle: {name}, Regelbeurteilung, Gesamturteil",
    en: "Source: {name}, regular evaluation, overall verdict",
  },

  "period_management.takeover.visibility_title": {
    de: "Sichtbarkeit im neuen Zeitraum",
    en: "Visibility in the new period",
  },
  "period_management.takeover.visibility_no_employees": {
    de: "Gilt nur, wenn Beschäftigte übernommen werden.",
    en: "Only applies when employees are taken over.",
  },
  "period_management.takeover.visibility.visible": { de: "Sofort sichtbar", en: "Visible immediately" },
  "period_management.takeover.visibility.visible.desc": {
    de: "Die übernommenen Beschäftigten erscheinen in allen Listen und Auswertungen des neuen Zeitraums.",
    en: "The employees taken over appear in every list and report of the new period.",
  },
  "period_management.takeover.visibility.hidden": { de: "Zunächst ausgeblendet", en: "Hidden at first" },
  "period_management.takeover.visibility.hidden.desc": {
    de: "Die Daten sind übernommen, die Personen erscheinen aber noch in keiner Liste. Sie lassen sich jederzeit einzeln oder gesammelt sichtbar schalten.",
    en: "The data is taken over but the people do not appear in any list yet. They can be made visible at any time, individually or all at once.",
  },

  /* ---- LOB only ---- */

  "period_management.takeover.intro_lob": {
    de: "Was soll aus dem Quellzeitraum {name} übernommen werden? Die Vorlagen setzen die Auswahl darunter.",
    en: "What should be taken over from the source period {name}? The templates set the selection below.",
  },
  "period_management.takeover.preset_desc.minimal.lob": {
    de: "Nur die Einstellungen. Keine Beschäftigten, keine Gruppen, keine Budgets.",
    en: "The settings only. No employees, no groups, no budgets.",
  },

  "period_management.takeover.area.settings.desc.lob": {
    de: "Ansichten, Filter, Leistungskartenvorlagen, Berechnungseinstellungen, Gesprächseinstellungen",
    en: "Views, filters, scorecard templates, calculation settings, interview settings",
  },
  "period_management.takeover.summary.area.settings.desc.lob.dropped": {
    de: "Ansichten, Filter, Berechnungs- und Gesprächseinstellungen werden nicht übernommen. Benötigte Leistungskartenvorlagen werden weiterhin automatisch mit den Leistungskarten übernommen.",
    en: "Views, filters, calculation and interview settings are not taken over. Required scorecard templates are still copied automatically with the scorecards.",
  },
  "period_management.takeover.area.budgets": { de: "Budgets", en: "Budgets" },
  "period_management.takeover.area.budgets.desc": {
    de: "Budgettöpfe und Budgeteinstellungen. Beträge und Auszahlungen werden nicht übernommen.",
    en: "Budget pots and budget settings. Amounts and payouts are not taken over.",
  },
  "period_management.takeover.area.scorecards": {
    de: "Leistungskarten und Zielvereinbarungen", en: "Scorecards and objectives",
  },
  "period_management.takeover.area.scorecards.desc": {
    de: "Leistungskarten mit ihren Zielvereinbarungen und Teamzielen. Bewertungen und Ergebnisse werden nie übernommen.",
    en: "Scorecards with their objectives and team objectives. Ratings and results are never taken over.",
  },
  "period_management.takeover.implicit.scorecard_templates": {
    de: "Leistungskartenvorlagen", en: "Scorecard templates",
  },
  "period_management.takeover.implicit.always_copied": {
    de: "immer übernommen", en: "always copied",
  },
  "period_management.takeover.implicit.scorecard_templates.desc": {
    de: "Alle Vorlagen, die von den übernommenen Leistungskarten oder Teamzielen verwendet werden, kommen automatisch mit – unabhängig von der Auswahl „Einstellungen“.",
    en: "Every template used by the copied scorecards or team objectives is copied automatically, regardless of the Settings selection.",
  },
  "period_management.takeover.scorecard_action_title": {
    de: "Umgang mit den Zielvereinbarungen", en: "How the objectives are handled",
  },

  "period_management.takeover.field.department": { de: "Abteilung", en: "Department" },
  "period_management.takeover.field.pay_grade": { de: "Entgeltgruppe", en: "Pay grade" },
  "period_management.takeover.field.pay_grade_level": { de: "Entgeltstufe", en: "Pay grade level" },
  "period_management.takeover.field.working_days_per_week": {
    de: "Arbeitstage pro Woche", en: "Working days per week",
  },
  "period_management.takeover.field.annual_compensation": { de: "Jahresentgelt", en: "Annual compensation" },
  "period_management.takeover.field.individual_pay_factor": {
    de: "Individueller Entgeltfaktor", en: "Individual pay factor",
  },
  "period_management.takeover.field.reference_values": {
    de: "Referenzwerte für LK-Vorlage, Budget und Gruppe",
    en: "Reference values for scorecard template, budget and group",
  },

  "period_management.takeover.never_body.lob": {
    de: "Berechnungsergebnis, Individueller Durchschnitt, Individuelle Deckelung, Manueller Mittelwert, Trend, Von der Auszahlung ausgeschlossen, Von der Bewertung ausgeschlossen, Fehlmonate, Fehltage sowie alle Bewertungen. Diese Werte gehören zum abgelaufenen Zeitraum und bleiben dort unverändert erhalten.",
    en: "Calculation result, individual average, individual ceiling, manual average value, trend, excluded from payout, excluded from evaluation, missing months, missing days and every rating. These values belong to the period that ended and stay there unchanged.",
  },

  /* ---- Summary before the period is created, both modules (LIX-1706) ---- */

  /* The same areas as above, worded for a screen where the selection is behind
     the user rather than in front of them. See summaryDescKey in takeoverConfig. */
  "period_management.takeover.summary.area.global_roles.desc": {
    de: "IT-Administration, Programmadministration, Moduladministration und Personalverwaltung. Gelten unabhängig vom Zeitraum und sind von Anfang an aktiv.",
    en: "IT administration, program administration, module administration and HR support. They apply independently of the period and are active from the start.",
  },
  "period_management.takeover.summary.area.groups.desc.db": {
    de: "Gruppen mit ihren Erst- und Zweitbeurteilenden, dazu die Kategorien von Gruppen und Beschäftigten.",
    en: "Groups with their first and second evaluators, plus the categories of groups and employees.",
  },
  "period_management.takeover.summary.area.groups.desc.lob": {
    de: "Gruppen mit ihren Bewertenden und Rezensierenden, dazu die Kategorien von Gruppen und Beschäftigten.",
    en: "Groups with their evaluators and reviewers, plus the categories of groups and employees.",
  },
  "period_management.takeover.summary.area.employees.desc": {
    de: "Die Personen selbst.",
    en: "The people themselves.",
  },

  "period_management.takeover.summary.lede": {
    de: "Prüfen Sie die Übernahme, bevor der Zeitraum entsteht.",
    en: "Check the takeover before the period comes into existence.",
  },
  "period_management.takeover.summary.head.source": { de: "Quellzeitraum", en: "Source period" },
  "period_management.takeover.summary.head.new": { de: "Neuer Zeitraum", en: "New period" },
  "period_management.takeover.summary.head.visibility": { de: "Sichtbarkeit", en: "Visibility" },

  "period_management.takeover.summary.carried_title": { de: "Wird übernommen", en: "Taken over" },
  "period_management.takeover.summary.dropped_title": {
    de: "Wird nicht übernommen", en: "Not taken over",
  },
  "period_management.takeover.summary.areas_one": { de: "1 Bereich", en: "1 area" },
  "period_management.takeover.summary.areas_other": { de: "{count} Bereiche", en: "{count} areas" },
  "period_management.takeover.summary.points_one": { de: "1 Punkt", en: "1 item" },
  "period_management.takeover.summary.points_other": { de: "{count} Punkte", en: "{count} items" },
  "period_management.takeover.summary.dropped_none": {
    de: "Nichts. Alles Auswählbare kommt mit. Was trotzdem nicht mitkommt, steht unten unter „Unabhängig von Ihrer Auswahl“.",
    en: 'Nothing. Everything selectable comes along. What does not come along regardless is listed below under "Independent of your selection".',
  },
  "period_management.takeover.summary.dropped_empty": {
    de: "Der neue Zeitraum startet hier leer.",
    en: "The new period starts empty here.",
  },
  "period_management.takeover.summary.dropped_employees": {
    de: "Der neue Zeitraum startet ohne Personen, damit auch ohne jede der {total} Angaben.",
    en: "The new period starts without people, and therefore without any of the {total} details.",
  },
  "period_management.takeover.summary.fields_carried": {
    de: "{count} von {total} Angaben kommen mit.",
    en: "{count} of {total} details come along.",
  },
  "period_management.takeover.summary.fields_show": {
    de: "Angaben anzeigen", en: "Show the details",
  },
  "period_management.takeover.summary.fields_dropped_title": {
    de: "{count} von {total} Angaben zu den Beschäftigten",
    en: "{count} of {total} employee details",
  },
  "period_management.takeover.summary.fields_dropped_desc": {
    de: "Die Personen kommen mit, diese Angaben nicht. Sie sind im neuen Zeitraum leer.",
    en: "The people come along, these details do not. They are empty in the new period.",
  },

  "period_management.takeover.summary.fixed_title": {
    de: "Unabhängig von Ihrer Auswahl", en: "Independent of your selection",
  },
  "period_management.takeover.summary.fixed_hint": {
    de: "Das gilt bei jeder Übernahme und lässt sich nicht einstellen.",
    en: "This holds for every takeover and cannot be configured.",
  },
  "period_management.takeover.summary.always_title": { de: "Immer vorhanden", en: "Always present" },
  "period_management.takeover.summary.free_title": {
    de: "Kommt immer mit", en: "Always comes along",
  },
  "period_management.takeover.summary.free_body": {
    de: "Individuell angelegte Felder, also die frei beschriftbaren Felder. Sie gehören zum Zeitraum und kommen bei jeder Übernahme mit. Sie lassen sich nicht abwählen und stehen deshalb nicht zur Auswahl.",
    en: "Individually created fields, the freely labelled ones. They belong to the period and come along with every takeover. They cannot be deselected and are therefore not offered as a choice.",
  },
  "period_management.takeover.summary.never_title": {
    de: "Wird nie übernommen", en: "Never taken over",
  },
  "period_management.takeover.summary.lookup_title": {
    de: "Wird nachgeschlagen", en: "Looked up",
  },
  /* Its own wording rather than the banner's: that one opens with "Der Wert",
     which has an antecedent under the heading it sits below on the selection
     step and none at all here. */
  "period_management.takeover.summary.lookup_body": {
    de: "Das vorherige Regelbeurteilungsergebnis wird bei jedem Aufruf aus dem Quellzeitraum gelesen und nicht kopiert. Ändert sich die Beurteilung dort später, zeigt der neue Zeitraum sofort den aktuellen Stand.",
    en: "The previous regular evaluation result is read from the source period every time it is shown, and is not copied. If that evaluation changes there later, the new period shows the current state immediately.",
  },

  "period_management.takeover.summary.confirm_body": {
    de: "Was jetzt nicht mitkommt, lässt sich später nicht nachholen. Der Zeitraum müsste neu angelegt werden.",
    en: "Whatever does not come along now cannot be added later. The period would have to be created again.",
  },
  "period_management.takeover.summary.confirm_tick": {
    de: "Ich habe die Übernahme geprüft.",
    en: "I have checked the takeover.",
  },

  "period_management.takeover.first_period": {
    de: "Keine. Die Einstellungen starten mit den Standardwerten, Beschäftigte kommen über den Import oder die Zuordnung zum Zeitraum hinzu.",
    en: "None. The settings start from their defaults; employees are added through the import or by assigning them to the period.",
  },
  "period_management.first.summary.lede": {
    de: "Prüfen Sie Name und Beurteilungszeitraum, bevor der erste Zeitraum des Moduls entsteht.",
    en: "Check the name and evaluation period before the module's first period comes into existence.",
  },
  "period_management.first.summary.head.module": { de: "Modul", en: "Module" },
  "period_management.first.summary.head.takeover": { de: "Übernahme", en: "Takeover" },
  "period_management.first.summary.takeover_none": { de: "keine", en: "none" },

  "period_management.step.form": { de: "Zeitraum und Übernahme", en: "Period and takeover" },
  "period_management.step.summary": { de: "Prüfen und anlegen", en: "Review and create" },

  "period_management.employees.title": { de: "Beschäftigte", en: "Employees" },
  "period_management.employees.activate": {
    de: "Beschäftigte im neuen Zeitraum direkt aktiv setzen",
    en: "Switch the employees active in the new period straight away",
  },
  "period_management.employees.activate_hint_on": {
    de: "Alle übernommenen Beschäftigten erscheinen sofort in der Übersicht des neuen Zeitraums.",
    en: "Every employee taken over appears in the overview of the new period straight away.",
  },
  "period_management.employees.activate_hint_off": {
    de: "Die Beschäftigten sind mit ihren Stammdaten im neuen Zeitraum vorhanden, erscheinen aber erst in der Übersicht, sobald sie aktiv gesetzt werden: einzeln über die Beschäftigtenübersicht oder gesammelt über den Abgleich mit dem Personalverwaltungssystem. Erfassen müssen Sie nichts erneut.",
    en: "The employees are present in the new period with their master data but only appear in the overview once they are switched active: one by one in the employee overview, or together through the comparison with the HR system. Nothing has to be entered again.",
  },

  "period_management.edit.title": { de: "{name} bearbeiten", en: "Edit {name}" },
  "period_management.edit.impact_live": {
    de: "Diese Periode ist produktiv. Änderungen wirken sofort auf alle Auswertungen und auf {count} aktive Beschäftigte.",
    en: "This period is in production. Changes take effect immediately on all evaluations and on {count} active employees.",
  },
  "period_management.edit.impact_draft": {
    de: "Entwurf: Änderungen wirken sich noch auf keine Auswertung aus.",
    en: "Draft: changes do not affect any evaluation yet.",
  },
  "period_management.edit.impact_archived": {
    de: "Archiviert: Änderungen betreffen nur historische Auswertungen.",
    en: "Archived: changes only affect historic evaluations.",
  },
  "period_management.edit.allowed_range": { de: "Erlaubt ist {range}.", en: "Allowed is {range}." },
  "period_management.edit.allowed_from": { de: "ab {date}", en: "from {date}" },
  "period_management.edit.allowed_to": { de: " bis {date}", en: " until {date}" },
  "period_management.edit.allowed_any": { de: "jedes Startdatum", en: "any start date" },
  "period_management.edit.duration_default": {
    de: "{months} Monate · Regellaufzeit",
    en: "{months} months · standard duration",
  },
  "period_management.edit.duration_custom": {
    de: "{months} Monate · abweichend von der Regellaufzeit",
    en: "{months} months · different from the standard duration",
  },
  "period_management.edit.overlap_previous": {
    de: "Überschneidung mit {name} (endet {date}). Zwei Perioden im Modul {module} dürfen sich nicht überlappen.",
    en: "Overlap with {name} (ends {date}). Two periods in module {module} may not overlap.",
  },
  "period_management.edit.overlap_next": {
    de: "Überschneidung mit {name} ({status}, beginnt {date}). Passen Sie zuerst die Nachfolgerin an.",
    en: "Overlap with {name} ({status}, starts {date}). Adjust the successor first.",
  },
  "period_management.edit.gap_before": {
    de: "Lücke von {days} vor dieser Periode: In dieser Zeit ist im Modul {module} keine Periode aktiv. Speichern bleibt möglich.",
    en: "Gap of {days} before this period: no period is active in module {module} during that time. Saving remains possible.",
  },
  "period_management.edit.gap_after": {
    de: "Lücke von {days} bis {name}. Speichern bleibt möglich.",
    en: "Gap of {days} until {name}. Saving remains possible.",
  },
  "period_management.edit.effect": {
    de: "Die Änderung wirkt auf alle Auswertungen dieser Periode und auf {count} aktive Beschäftigte.",
    en: "The change affects all evaluations of this period and {count} active employees.",
  },
  "period_management.edit.takeover_scope": { de: "Übernahmeumfang", en: "Takeover scope" },
  "period_management.edit.not_changeable": { de: "nachträglich nicht änderbar", en: "cannot be changed afterwards" },
  "period_management.edit.maintained_in_hr": {
    de: "wird in der Personalverwaltung gepflegt",
    en: "maintained in the personnel administration",
  },

  "period_management.savebar.no_changes": { de: "Keine Änderungen", en: "No changes" },
  "period_management.savebar.changes_one": { de: "1 Änderung", en: "1 change" },
  "period_management.savebar.changes_other": { de: "{count} Änderungen", en: "{count} changes" },
  "period_management.savebar.hint_none": {
    de: "Ändern Sie ein Feld, um zu speichern.",
    en: "Change a field to save.",
  },
  "period_management.savebar.hint_ready": {
    de: "Wirkt nach dem Speichern sofort.",
    en: "Takes effect immediately after saving.",
  },
  "period_management.savebar.hint_blocked": {
    de: "Bitte zuerst den Konflikt oben auflösen.",
    en: "Please resolve the conflict above first.",
  },

  "period_management.button.create": { de: "Anlegen", en: "Create" },
  "period_management.button.continue": { de: "Weiter zur Prüfung", en: "Continue to the review" },
  "period_management.button.back": { de: "Zurück zur Auswahl", en: "Back to the selection" },
  "period_management.button.create_period": { de: "Zeitraum anlegen", en: "Create period" },
  "period_management.button.cancel": { de: "Abbrechen", en: "Cancel" },
  "period_management.button.save": { de: "Speichern", en: "Save" },
  "period_management.button.discard": { de: "Verwerfen", en: "Discard" },

  "period_management.toast.created": {
    de: "Entwurf {name} angelegt, die Datenübernahme läuft.",
    en: "Draft {name} created, the takeover is running.",
  },
  "period_management.toast.discarded": { de: "Änderungen verworfen.", en: "Changes discarded." },
};

const applyReplace = (text, replace) => Object.entries(replace).reduce(
  (result, [token, value]) => result.replace(new RegExp(`\\{${token}\\}`, "g"), value),
  text,
);

/**
 * The app translator only substitutes placeholders when the key exists in the
 * dictionary, so a fallback has to be substituted before it is handed over.
 */
const fallbackFor = (key, lang, replace) => {
  const entry = STRINGS[key];
  if (!entry) return null;

  return applyReplace(lang === "en" ? (entry.en ?? entry.de) : entry.de, replace);
};

/**
 * pt() renders through the app dictionary and falls back to the wording above.
 * ps() gives the plain string for attributes such as title or aria-label.
 */
export function usePeriodStrings() {
  const lang = useSelector(getLang);
  const { editableTranslate, translate } = useTranslation();

  const pt = (key, replace = {}) => editableTranslate(key, fallbackFor(key, lang, replace), replace);
  const ps = (key, replace = {}) => translate(key, fallbackFor(key, lang, replace), replace);

  return { pt, ps, lang };
}

export default STRINGS;
